import React, { useState, useEffect, useCallback } from 'react';

const API = '';

const STAGES = ['New Lead', 'Contacted', 'Demo Scheduled', 'Proposal Sent', 'Closed Won', 'Closed Lost'];
const STAGE_COLORS = {
  'New Lead':       { bg: '#eff6ff', border: '#3b82f6', text: '#1d4ed8', dot: '#3b82f6' },
  'Contacted':      { bg: '#f0fdf4', border: '#22c55e', text: '#15803d', dot: '#22c55e' },
  'Demo Scheduled': { bg: '#fefce8', border: '#eab308', text: '#854d0e', dot: '#eab308' },
  'Proposal Sent':  { bg: '#fff7ed', border: '#f97316', text: '#c2410c', dot: '#f97316' },
  'Closed Won':     { bg: '#f0fdf4', border: '#16a34a', text: '#14532d', dot: '#16a34a' },
  'Closed Lost':    { bg: '#fef2f2', border: '#ef4444', text: '#991b1b', dot: '#ef4444' },
};
const SOURCE_LABELS = {
  launchsched_demo: 'LaunchSched',
  schoollaunch_demo: 'SchoolLaunch',
  gainlens_demo: 'GainLens',
  manual: 'Manual',
};
const SOURCE_COLORS = {
  launchsched_demo: '#1e40af',
  schoollaunch_demo: '#7c3aed',
  gainlens_demo: '#0891b2',
  manual: '#6b7280',
};
const METHODS = ['Email', 'Phone', 'Text', 'Video Call', 'In Person', 'Other'];

function apiCall(path, method = 'GET', body = null, token = null) {
  const opts = {
    method,
    headers: { 'Content-Type': 'application/json' },
  };
  if (token) opts.headers['Authorization'] = `Bearer ${token}`;
  if (body) opts.body = JSON.stringify(body);
  return fetch(`${API}${path}`, opts).then(r => r.json());
}

// ============================================================
// TOAST
// ============================================================
function Toast({ toasts, remove }) {
  return (
    <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9999, display: 'flex', flexDirection: 'column', gap: 10 }}>
      {toasts.map(t => (
        <div key={t.id} onClick={() => remove(t.id)} style={{
          background: t.type === 'error' ? '#fef2f2' : '#f0fdf4',
          border: `1px solid ${t.type === 'error' ? '#fca5a5' : '#86efac'}`,
          color: t.type === 'error' ? '#991b1b' : '#166534',
          borderRadius: 8, padding: '10px 16px', fontSize: 13, cursor: 'pointer',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)', minWidth: 260, maxWidth: 360,
        }}>
          <div style={{ fontWeight: 600, marginBottom: 2 }}>{t.title}</div>
          {t.message && <div style={{ opacity: 0.85 }}>{t.message}</div>}
        </div>
      ))}
    </div>
  );
}

function useToast() {
  const [toasts, setToasts] = useState([]);
  const show = useCallback((title, message = '', type = 'success') => {
    const id = Date.now();
    setToasts(p => [...p, { id, title, message, type }]);
    setTimeout(() => setToasts(p => p.filter(t => t.id !== id)), 3500);
  }, []);
  const remove = useCallback(id => setToasts(p => p.filter(t => t.id !== id)), []);
  return { toasts, show, remove };
}

// ============================================================
// FORCE PASSWORD RESET
// ============================================================
function ForcePasswordReset({ token, onComplete }) {
  const [form, setForm] = useState({ newPassword: '', confirmPassword: '' });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    setError('');
    if (!form.newPassword || form.newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (form.newPassword !== form.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setSaving(true);
    try {
      const d = await apiCall('/api/auth/change-password', 'POST', {
        currentPassword: form.newPassword,
        newPassword: form.newPassword,
      }, token);
      // Use direct update endpoint since we don't have old password
      const d2 = await fetch('/api/auth/reset-forced', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ newPassword: form.newPassword }),
      }).then(r => r.json());
      if (d2.success) {
        onComplete();
      } else {
        setError(d2.error || 'Failed to update password');
      }
    } catch {
      setError('Connection error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #0f172a 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    }}>
      <div style={{ background: 'white', borderRadius: 16, padding: '48px 40px', width: 380, boxShadow: '0 24px 64px rgba(0,0,0,0.4)' }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ fontSize: 11, letterSpacing: 3, color: '#6b7280', textTransform: 'uppercase', marginBottom: 8 }}>SchoolLaunch</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#0f172a' }}>Set Your Password</div>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 8, lineHeight: 1.5 }}>
            For security, you must set a new password before continuing.
          </div>
        </div>

        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', color: '#991b1b', borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 20 }}>
            {error}
          </div>
        )}

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6, letterSpacing: 0.5 }}>NEW PASSWORD</label>
          <input
            type="password" value={form.newPassword}
            onChange={e => setForm(p => ({ ...p, newPassword: e.target.value }))}
            style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
            placeholder="At least 8 characters"
          />
        </div>
        <div style={{ marginBottom: 24 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6, letterSpacing: 0.5 }}>CONFIRM PASSWORD</label>
          <input
            type="password" value={form.confirmPassword}
            onChange={e => setForm(p => ({ ...p, confirmPassword: e.target.value }))}
            onKeyDown={e => e.key === 'Enter' && handleSubmit()}
            style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
            placeholder="Repeat password"
          />
        </div>
        <button onClick={handleSubmit} disabled={saving} style={{
          width: '100%', padding: '12px', background: '#1e3a5f', color: 'white',
          border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer',
          opacity: saving ? 0.7 : 1,
        }}>
          {saving ? 'Saving...' : 'Set Password and Continue'}
        </button>
      </div>
    </div>
  );
}

// ============================================================
// LOGIN
// ============================================================
function LoginPage({ onLogin }) {
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setError('');
    if (!form.email || !form.password) { setError('Email and password required'); return; }
    setLoading(true);
    try {
      const data = await apiCall('/api/auth/login', 'POST', form);
      if (data.success) {
        localStorage.setItem('pipeline_token', data.token);
        onLogin(data.user, data.token);
      } else {
        setError(data.error || 'Login failed');
      }
    } catch {
      setError('Unable to connect to server');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #0f172a 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    }}>
      <div style={{ background: 'white', borderRadius: 16, padding: '48px 40px', width: 380, boxShadow: '0 24px 64px rgba(0,0,0,0.4)' }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ fontSize: 11, letterSpacing: 3, color: '#6b7280', textTransform: 'uppercase', marginBottom: 8 }}>SchoolLaunch</div>
          <div style={{ fontSize: 26, fontWeight: 700, color: '#0f172a' }}>Pipeline CRM</div>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 6 }}>Sign in to your account</div>
        </div>

        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', color: '#991b1b', borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 20 }}>
            {error}
          </div>
        )}

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6, letterSpacing: 0.5 }}>EMAIL</label>
          <input
            type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
            onKeyDown={e => e.key === 'Enter' && handleSubmit()}
            style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', outline: 'none' }}
            placeholder="you@example.com"
          />
        </div>
        <div style={{ marginBottom: 24 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6, letterSpacing: 0.5 }}>PASSWORD</label>
          <input
            type="password" value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
            onKeyDown={e => e.key === 'Enter' && handleSubmit()}
            style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', outline: 'none' }}
            placeholder="Password"
          />
        </div>
        <button onClick={handleSubmit} disabled={loading} style={{
          width: '100%', padding: '12px', background: '#1e3a5f', color: 'white',
          border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer',
          opacity: loading ? 0.7 : 1,
        }}>
          {loading ? 'Signing in...' : 'Sign In'}
        </button>
      </div>
    </div>
  );
}

// ============================================================
// STAGE BADGE
// ============================================================
function StageBadge({ stage, small }) {
  const c = STAGE_COLORS[stage] || { bg: '#f3f4f6', border: '#9ca3af', text: '#374151', dot: '#9ca3af' };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      background: c.bg, border: `1px solid ${c.border}`, color: c.text,
      borderRadius: 20, padding: small ? '2px 8px' : '4px 10px', fontSize: small ? 11 : 12, fontWeight: 600,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: c.dot, flexShrink: 0 }} />
      {stage}
    </span>
  );
}

// ============================================================
// SOURCE BADGE
// ============================================================
function SourceBadge({ source }) {
  const color = SOURCE_COLORS[source] || '#6b7280';
  return (
    <span style={{
      display: 'inline-block', background: color + '18', border: `1px solid ${color}44`,
      color, borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 600,
    }}>
      {SOURCE_LABELS[source] || source}
    </span>
  );
}

// ============================================================
// LEAD FORM MODAL
// ============================================================
function LeadFormModal({ lead, token, users, onSave, onClose }) {
  const [form, setForm] = useState({
    name: lead?.name || '',
    email: lead?.email || '',
    org_name: lead?.org_name || '',
    phone: lead?.phone || '',
    title: lead?.title || '',
    stage: lead?.stage || 'New Lead',
    source: lead?.source || 'manual',
    notes: lead?.notes || '',
    follow_up_date: lead?.follow_up_date ? lead.follow_up_date.split('T')[0] : '',
    assigned_to: lead?.assigned_to || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const isEdit = !!lead?.id;

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Name is required'); return; }
    setSaving(true);
    try {
      const data = isEdit
        ? await apiCall(`/api/leads/${lead.id}`, 'PUT', form, token)
        : await apiCall('/api/leads', 'POST', form, token);
      if (data.success) { onSave(data.lead); }
      else { setError(data.error || 'Save failed'); }
    } catch { setError('Connection error'); }
    finally { setSaving(false); }
  };

  const F = ({ label, children }) => (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', letterSpacing: 0.5, marginBottom: 5, textTransform: 'uppercase' }}>{label}</label>
      {children}
    </div>
  );
  const inp = { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box', outline: 'none' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onClick={onClose}>
      <div style={{ background: 'white', borderRadius: 12, width: '100%', maxWidth: 520, maxHeight: '90vh', overflow: 'auto', padding: 28 }}
        onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#0f172a' }}>{isEdit ? 'Edit Lead' : 'Add Lead'}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#9ca3af' }}>x</button>
        </div>

        {error && <div style={{ background: '#fef2f2', color: '#991b1b', borderRadius: 6, padding: '8px 12px', fontSize: 12, marginBottom: 16 }}>{error}</div>}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <F label="Name *"><input style={inp} value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} /></F>
          </div>
          <F label="Email"><input style={inp} type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} /></F>
          <F label="Phone"><input style={inp} value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} /></F>
          <F label="Organization"><input style={inp} value={form.org_name} onChange={e => setForm(p => ({ ...p, org_name: e.target.value }))} /></F>
          <F label="Title"><input style={inp} value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} /></F>
          <F label="Stage">
            <select style={inp} value={form.stage} onChange={e => setForm(p => ({ ...p, stage: e.target.value }))}>
              {STAGES.map(s => <option key={s}>{s}</option>)}
            </select>
          </F>
          <F label="Source">
            <select style={inp} value={form.source} onChange={e => setForm(p => ({ ...p, source: e.target.value }))}>
              {Object.entries(SOURCE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </F>
          <F label="Follow-up Date"><input style={inp} type="date" value={form.follow_up_date} onChange={e => setForm(p => ({ ...p, follow_up_date: e.target.value }))} /></F>
          <F label="Assigned To">
            <select style={inp} value={form.assigned_to} onChange={e => setForm(p => ({ ...p, assigned_to: e.target.value }))}>
              <option value="">Unassigned</option>
              {(users || []).map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </F>
          <div style={{ gridColumn: '1 / -1' }}>
            <F label="Notes">
              <textarea style={{ ...inp, minHeight: 80, resize: 'vertical' }} value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} />
            </F>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
          <button onClick={handleSave} disabled={saving} style={{
            flex: 1, padding: '10px', background: '#1e3a5f', color: 'white',
            border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
          }}>
            {saving ? 'Saving...' : (isEdit ? 'Save Changes' : 'Add Lead')}
          </button>
          <button onClick={onClose} style={{ padding: '10px 20px', background: '#f1f5f9', border: 'none', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// LEAD DETAIL PANEL
// ============================================================
function LeadDetail({ leadId, token, users, showToast, onClose, onUpdated }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archiveReason, setArchiveReason] = useState('');
  const [archiving, setArchiving] = useState(false);
  const [contactForm, setContactForm] = useState({ contact_date: new Date().toISOString().split('T')[0], method: 'Email', outcome: '', notes: '' });
  const [addingContact, setAddingContact] = useState(false);
  const [savingContact, setSavingContact] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const d = await apiCall(`/api/leads/${leadId}`, 'GET', null, token);
    if (d.success) setData(d);
    setLoading(false);
  }, [leadId, token]);

  useEffect(() => { load(); }, [load]);

  const handleContactSave = async () => {
    setSavingContact(true);
    const d = await apiCall(`/api/leads/${leadId}/contacts`, 'POST', contactForm, token);
    if (d.success) {
      showToast('Contact logged');
      setAddingContact(false);
      setContactForm({ contact_date: new Date().toISOString().split('T')[0], method: 'Email', outcome: '', notes: '' });
      load();
      onUpdated && onUpdated();
    } else {
      showToast('Error', d.error, 'error');
    }
    setSavingContact(false);
  };

  const handleStageChange = async (stage) => {
    const d = await apiCall(`/api/leads/${leadId}/stage`, 'PATCH', { stage }, token);
    if (d.success) {
      showToast('Stage updated');
      load();
      onUpdated && onUpdated();
    }
  };

  const handleArchive = async () => {
    if (!archiveReason.trim()) return;
    setArchiving(true);
    const d = await apiCall(`/api/leads/${leadId}/archive`, 'PATCH', { reason: archiveReason }, token);
    if (d.success) {
      showToast('Lead archived');
      setArchiveOpen(false);
      setArchiveReason('');
      onClose();
      onUpdated && onUpdated();
    } else { showToast('Error', d.error, 'error'); }
    setArchiving(false);
  };

  const inp = { width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };
  const label = { fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4, display: 'block' };

  if (loading) return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'white', borderRadius: 12, padding: 40, color: '#6b7280' }}>Loading...</div>
    </div>
  );

  if (!data) return null;
  const { lead, contacts, activity } = data;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end' }}
      onClick={onClose}>
      <div style={{ background: 'white', width: '100%', maxWidth: 560, height: '100vh', overflow: 'auto', padding: 28, boxShadow: '-8px 0 32px rgba(0,0,0,0.15)' }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#0f172a' }}>{lead.name}</div>
            {lead.org_name && <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>{lead.title ? `${lead.title}, ` : ''}{lead.org_name}</div>}
            <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
              <StageBadge stage={lead.stage} small />
              <SourceBadge source={lead.source} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setEditOpen(true)} style={{ padding: '6px 14px', background: '#1e3a5f', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>Edit</button>
            <button onClick={() => setArchiveOpen(true)} style={{ padding: '6px 14px', background: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>Archive</button>
            <button onClick={onClose} style={{ padding: '6px 12px', background: '#f1f5f9', border: 'none', borderRadius: 6, fontSize: 14, cursor: 'pointer' }}>x</button>
          </div>
        </div>

        {/* Contact info */}
        <div style={{ background: '#f8fafc', borderRadius: 10, padding: '14px 16px', marginBottom: 20, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {lead.email && <div><span style={label}>Email</span><a href={`mailto:${lead.email}`} style={{ fontSize: 13, color: '#1e40af' }}>{lead.email}</a></div>}
          {lead.phone && <div><span style={label}>Phone</span><a href={`tel:${lead.phone}`} style={{ fontSize: 13, color: '#1e40af' }}>{lead.phone}</a></div>}
          {lead.follow_up_date && <div><span style={label}>Follow-up</span><span style={{ fontSize: 13, color: '#374151' }}>{new Date(lead.follow_up_date).toLocaleDateString()}</span></div>}
          {lead.assigned_name && <div><span style={label}>Assigned To</span><span style={{ fontSize: 13, color: '#374151' }}>{lead.assigned_name}</span></div>}
        </div>

        {/* Stage change */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Move Stage</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {STAGES.map(s => (
              <button key={s} onClick={() => handleStageChange(s)} style={{
                padding: '5px 12px', borderRadius: 20, fontSize: 12, cursor: 'pointer', fontWeight: 500,
                background: lead.stage === s ? STAGE_COLORS[s].bg : '#f8fafc',
                border: `1px solid ${lead.stage === s ? STAGE_COLORS[s].border : '#e2e8f0'}`,
                color: lead.stage === s ? STAGE_COLORS[s].text : '#64748b',
              }}>{s}</button>
            ))}
          </div>
        </div>

        {/* Notes */}
        {lead.notes && (
          <div style={{ marginBottom: 20, padding: '12px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#92400e', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Notes</div>
            <div style={{ fontSize: 13, color: '#451a03', lineHeight: 1.5 }}>{lead.notes}</div>
          </div>
        )}

        {/* Contact log */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>Contact Log</div>
            <button onClick={() => setAddingContact(p => !p)} style={{
              padding: '5px 12px', background: '#1e3a5f', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer',
            }}>+ Log Contact</button>
          </div>

          {addingContact && (
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 16, marginBottom: 12 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
                <div style={{ marginBottom: 10 }}>
                  <label style={label}>Date</label>
                  <input type="date" style={inp} value={contactForm.contact_date} onChange={e => setContactForm(p => ({ ...p, contact_date: e.target.value }))} />
                </div>
                <div style={{ marginBottom: 10 }}>
                  <label style={label}>Method</label>
                  <select style={inp} value={contactForm.method} onChange={e => setContactForm(p => ({ ...p, method: e.target.value }))}>
                    {METHODS.map(m => <option key={m}>{m}</option>)}
                  </select>
                </div>
                <div style={{ gridColumn: '1 / -1', marginBottom: 10 }}>
                  <label style={label}>Outcome</label>
                  <input style={inp} placeholder="e.g. Left voicemail, Scheduled follow-up..." value={contactForm.outcome} onChange={e => setContactForm(p => ({ ...p, outcome: e.target.value }))} />
                </div>
                <div style={{ gridColumn: '1 / -1', marginBottom: 10 }}>
                  <label style={label}>Notes</label>
                  <textarea style={{ ...inp, minHeight: 60 }} value={contactForm.notes} onChange={e => setContactForm(p => ({ ...p, notes: e.target.value }))} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={handleContactSave} disabled={savingContact} style={{ padding: '7px 16px', background: '#16a34a', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>Save</button>
                <button onClick={() => setAddingContact(false)} style={{ padding: '7px 14px', background: '#f1f5f9', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>Cancel</button>
              </div>
            </div>
          )}

          {contacts.length === 0 && !addingContact && (
            <div style={{ color: '#94a3b8', fontSize: 13, fontStyle: 'italic', padding: '8px 0' }}>No contacts logged yet.</div>
          )}
          {contacts.map(c => (
            <div key={c.id} style={{ borderLeft: '3px solid #3b82f6', paddingLeft: 12, marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#1e40af' }}>{c.method} &bull; {new Date(c.contact_date).toLocaleDateString()}</div>
                {c.logged_by_name && <div style={{ fontSize: 11, color: '#94a3b8' }}>by {c.logged_by_name}</div>}
              </div>
              {c.outcome && <div style={{ fontSize: 13, color: '#374151', marginTop: 2 }}>{c.outcome}</div>}
              {c.notes && <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{c.notes}</div>}
            </div>
          ))}
        </div>

        {/* Activity */}
        {activity.length > 0 && (
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 10 }}>Activity</div>
            {activity.map(a => (
              <div key={a.id} style={{ fontSize: 12, color: '#64748b', paddingBottom: 6, borderBottom: '1px solid #f1f5f9', marginBottom: 6 }}>
                <span style={{ color: '#374151', fontWeight: 500 }}>{a.action.replace(/_/g, ' ')}</span>
                {a.user_name && ` by ${a.user_name}`}
                <span style={{ float: 'right', color: '#94a3b8' }}>{new Date(a.created_at).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {archiveOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
          onClick={() => setArchiveOpen(false)}>
          <div style={{ background: 'white', borderRadius: 12, padding: 28, width: '100%', maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>Archive Lead</div>
            <div style={{ fontSize: 13, color: '#64748b', marginBottom: 20 }}>This lead will be removed from the active pipeline. You can restore it at any time.</div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Reason *</label>
              <select style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }}
                value={archiveReason} onChange={e => setArchiveReason(e.target.value)}>
                <option value="">Select a reason...</option>
                <option>Not a fit</option>
                <option>No response</option>
                <option>Lost to competitor</option>
                <option>Budget constraints</option>
                <option>Timing not right</option>
                <option>Duplicate lead</option>
                <option>Other</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={handleArchive} disabled={archiving || !archiveReason} style={{ flex: 1, padding: '9px', background: '#dc2626', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: (!archiveReason || archiving) ? 0.5 : 1 }}>
                {archiving ? 'Archiving...' : 'Archive Lead'}
              </button>
              <button onClick={() => setArchiveOpen(false)} style={{ padding: '9px 18px', background: '#f1f5f9', border: 'none', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {editOpen && (
        <LeadFormModal
          lead={lead} token={token} users={users}
          onSave={(updated) => { setEditOpen(false); load(); onUpdated && onUpdated(); }}
          onClose={() => setEditOpen(false)}
        />
      )}
    </div>
  );
}

// ============================================================
// PIPELINE (KANBAN) VIEW
// ============================================================
function PipelineView({ token, users, showToast, onLeadClick }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterSource, setFilterSource] = useState('');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filterSource) params.set('source', filterSource);
    if (search) params.set('search', search);
    const d = await apiCall(`/api/leads?${params}`, 'GET', null, token);
    if (d.success) setLeads(d.leads);
    setLoading(false);
  }, [token, filterSource, search]);

  useEffect(() => { load(); }, [load]);

  const stageLeads = (stage) => leads.filter(l => l.stage === stage);

  const handleStageChange = async (leadId, newStage) => {
    await apiCall(`/api/leads/${leadId}/stage`, 'PATCH', { stage: newStage }, token);
    showToast('Stage updated');
    load();
  };

  return (
    <div>
      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          placeholder="Search leads..."
          value={search} onChange={e => setSearch(e.target.value)}
          style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, width: 220 }}
        />
        <select value={filterSource} onChange={e => setFilterSource(e.target.value)}
          style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13 }}>
          <option value="">All Sources</option>
          {Object.entries(SOURCE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <div style={{ fontSize: 13, color: '#64748b' }}>{leads.length} lead{leads.length !== 1 ? 's' : ''}</div>
      </div>

      {loading ? (
        <div style={{ color: '#64748b', fontSize: 14 }}>Loading...</div>
      ) : (
        <div style={{ display: 'flex', gap: 14, overflowX: 'auto', paddingBottom: 16 }}>
          {STAGES.map(stage => {
            const c = STAGE_COLORS[stage];
            const items = stageLeads(stage);
            return (
              <div key={stage} style={{ minWidth: 230, flex: '0 0 230px' }}>
                <div style={{
                  background: c.bg, border: `1px solid ${c.border}`,
                  borderRadius: '8px 8px 0 0', padding: '10px 12px',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: c.text, textTransform: 'uppercase', letterSpacing: 0.5 }}>{stage}</span>
                  <span style={{ background: c.border, color: 'white', borderRadius: 10, padding: '1px 7px', fontSize: 11, fontWeight: 700 }}>{items.length}</span>
                </div>
                <div style={{ background: '#f8fafc', border: `1px solid ${c.border}`, borderTop: 'none', borderRadius: '0 0 8px 8px', minHeight: 100, padding: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {items.map(lead => (
                    <div key={lead.id} onClick={() => onLeadClick(lead.id)} style={{
                      background: 'white', borderRadius: 8, padding: '10px 12px', cursor: 'pointer',
                      border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                      transition: 'box-shadow 0.15s',
                    }}
                      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'}
                      onMouseLeave={e => e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)'}
                    >
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', marginBottom: 2 }}>{lead.name}</div>
                      {lead.org_name && <div style={{ fontSize: 11, color: '#64748b', marginBottom: 6 }}>{lead.org_name}</div>}
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 6 }}>
                        <SourceBadge source={lead.source} />
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        {lead.follow_up_date ? (
                          <span style={{ fontSize: 10, color: new Date(lead.follow_up_date) < new Date() ? '#dc2626' : '#64748b' }}>
                            Follow-up: {new Date(lead.follow_up_date).toLocaleDateString()}
                          </span>
                        ) : <span />}
                        {lead.contact_count > 0 && (
                          <span style={{ fontSize: 10, color: '#94a3b8' }}>{lead.contact_count} contact{lead.contact_count !== 1 ? 's' : ''}</span>
                        )}
                      </div>
                    </div>
                  ))}
                  {items.length === 0 && (
                    <div style={{ color: '#cbd5e1', fontSize: 12, textAlign: 'center', padding: '16px 0' }}>No leads</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================================================
// LIST VIEW
// ============================================================
function ListView({ token, users, showToast, onLeadClick, onNewLead }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStage, setFilterStage] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filterStage) params.set('stage', filterStage);
    if (filterSource) params.set('source', filterSource);
    if (search) params.set('search', search);
    const d = await apiCall(`/api/leads?${params}`, 'GET', null, token);
    if (d.success) setLeads(d.leads);
    setLoading(false);
  }, [token, filterStage, filterSource, search]);

  useEffect(() => { load(); }, [load]);

  const th = { padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap' };
  const td = { padding: '10px 12px', fontSize: 13, color: '#374151', borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle' };

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, alignItems: 'center', flexWrap: 'wrap' }}>
        <input placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)}
          style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, width: 200 }} />
        <select value={filterStage} onChange={e => setFilterStage(e.target.value)}
          style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13 }}>
          <option value="">All Stages</option>
          {STAGES.map(s => <option key={s}>{s}</option>)}
        </select>
        <select value={filterSource} onChange={e => setFilterSource(e.target.value)}
          style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13 }}>
          <option value="">All Sources</option>
          {Object.entries(SOURCE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <div style={{ fontSize: 13, color: '#64748b', marginLeft: 'auto' }}>{leads.length} leads</div>
        <button onClick={onNewLead} style={{ padding: '8px 16px', background: '#1e3a5f', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>+ Add Lead</button>
      </div>

      {loading ? <div style={{ color: '#64748b' }}>Loading...</div> : (
        <div style={{ background: 'white', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: '#f8fafc' }}>
              <tr>
                <th style={th}>Name</th>
                <th style={th}>Organization</th>
                <th style={th}>Stage</th>
                <th style={th}>Source</th>
                <th style={th}>Last Contact</th>
                <th style={th}>Follow-up</th>
                <th style={th}>Assigned</th>
              </tr>
            </thead>
            <tbody>
              {leads.length === 0 && (
                <tr><td colSpan={7} style={{ ...td, textAlign: 'center', color: '#94a3b8', fontStyle: 'italic' }}>No leads found</td></tr>
              )}
              {leads.map(lead => (
                <tr key={lead.id} onClick={() => onLeadClick(lead.id)} style={{ cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}>
                  <td style={td}>
                    <div style={{ fontWeight: 600, color: '#0f172a' }}>{lead.name}</div>
                    {lead.email && <div style={{ fontSize: 11, color: '#94a3b8' }}>{lead.email}</div>}
                  </td>
                  <td style={td}>{lead.org_name || <span style={{ color: '#cbd5e1' }}>--</span>}</td>
                  <td style={td}><StageBadge stage={lead.stage} small /></td>
                  <td style={td}><SourceBadge source={lead.source} /></td>
                  <td style={td}>
                    {lead.last_contact_date ? new Date(lead.last_contact_date).toLocaleDateString() : <span style={{ color: '#cbd5e1' }}>None</span>}
                    {lead.contact_count > 0 && <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 4 }}>({lead.contact_count})</span>}
                  </td>
                  <td style={{ ...td, color: lead.follow_up_date && new Date(lead.follow_up_date) < new Date() ? '#dc2626' : '#374151' }}>
                    {lead.follow_up_date ? new Date(lead.follow_up_date).toLocaleDateString() : <span style={{ color: '#cbd5e1' }}>--</span>}
                  </td>
                  <td style={{ ...td, color: '#64748b' }}>{lead.assigned_name || <span style={{ color: '#cbd5e1' }}>--</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============================================================
// DRILL-DOWN SLIDE-OUT PANEL
// ============================================================
function DrillDownPanel({ title, leads, onLeadClick, onClose }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 2000, display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end' }}
      onClick={onClose}>
      <div style={{ background: 'white', width: '100%', maxWidth: 480, height: '100vh', overflow: 'auto', padding: 28, boxShadow: '-8px 0 32px rgba(0,0,0,0.15)' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: '#0f172a' }}>{title}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#9ca3af' }}>x</button>
        </div>
        {leads.length === 0 && (
          <div style={{ color: '#94a3b8', fontSize: 13, fontStyle: 'italic' }}>No leads in this group.</div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {leads.map(lead => (
            <div key={lead.id} onClick={() => { onLeadClick(lead.id); onClose(); }} style={{
              background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 14px', cursor: 'pointer',
            }}
              onMouseEnter={e => e.currentTarget.style.background = '#eff6ff'}
              onMouseLeave={e => e.currentTarget.style.background = '#f8fafc'}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontWeight: 600, color: '#0f172a', fontSize: 13 }}>{lead.name}</div>
                  {lead.org_name && <div style={{ fontSize: 11, color: '#64748b', marginTop: 1 }}>{lead.org_name}</div>}
                </div>
                <div style={{ display: 'flex', gap: 6, flexDirection: 'column', alignItems: 'flex-end' }}>
                  <StageBadge stage={lead.stage} small />
                  <SourceBadge source={lead.source} />
                </div>
              </div>
              {lead.follow_up_date && (
                <div style={{ fontSize: 11, color: new Date(lead.follow_up_date) < new Date() ? '#dc2626' : '#64748b', marginTop: 6 }}>
                  Follow-up: {new Date(lead.follow_up_date).toLocaleDateString()}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// DASHBOARD
// ============================================================
function Dashboard({ token, showToast, onImport, onLeadClick }) {
  const [summary, setSummary] = useState({});
  const [allLeads, setAllLeads] = useState([]);
  const [followUps, setFollowUps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importingSource, setImportingSource] = useState(null);
  const [drillDown, setDrillDown] = useState(null); // { title, leads }

  const load = useCallback(async () => {
    setLoading(true);
    const [s, f, l] = await Promise.all([
      apiCall('/api/leads/pipeline-summary', 'GET', null, token),
      apiCall('/api/leads/follow-ups/due', 'GET', null, token),
      apiCall('/api/leads', 'GET', null, token),
    ]);
    if (s.success) setSummary(s.summary);
    if (f.success) setFollowUps(f.leads);
    if (l.success) setAllLeads(l.leads);
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handleImport = async (source, label) => {
    setImportingSource(source);
    const d = await apiCall('/api/import/demo-leads', 'POST', { source }, token);
    if (d.success) {
      showToast('Import complete', `${d.imported} new lead${d.imported !== 1 ? 's' : ''} imported from ${label}`);
      load();
      onImport && onImport();
    } else {
      showToast('Import failed', d.error, 'error');
    }
    setImportingSource(null);
  };

  const total = STAGES.reduce((n, s) => n + (summary[s] || 0), 0);
  const won = summary['Closed Won'] || 0;
  const active = total - (summary['Closed Won'] || 0) - (summary['Closed Lost'] || 0);

  const openDrillDown = (title, filterFn) => {
    setDrillDown({ title, leads: allLeads.filter(filterFn) });
  };

  const StatCard = ({ label, value, color, onClick }) => (
    <div onClick={onClick} style={{
      background: 'white', borderRadius: 10, padding: '18px 20px', border: '1px solid #e2e8f0',
      flex: 1, minWidth: 120, cursor: onClick ? 'pointer' : 'default',
      transition: 'box-shadow 0.15s',
    }}
      onMouseEnter={e => { if (onClick) e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'; }}
      onMouseLeave={e => e.currentTarget.style.boxShadow = ''}
    >
      <div style={{ fontSize: 28, fontWeight: 700, color: color || '#0f172a' }}>{value}</div>
      <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{label}</div>
      {onClick && <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>Click to view</div>}
    </div>
  );

  const IMPORT_SOURCES = [
    { source: 'launchsched_demo', label: 'LaunchSched Demo', color: '#1e40af' },
    { source: 'schoollaunch_demo', label: 'SchoolLaunch Demo', color: '#7c3aed' },
    { source: 'gainlens_demo', label: 'GainLens Demo', color: '#0891b2' },
  ];

  return (
    <div>
      {loading ? <div style={{ color: '#64748b' }}>Loading...</div> : (
        <>
          <div style={{ display: 'flex', gap: 14, marginBottom: 24, flexWrap: 'wrap' }}>
            <StatCard label="Total Leads" value={total}
              onClick={() => openDrillDown('All Leads', () => true)} />
            <StatCard label="Active" value={active} color="#1e40af"
              onClick={() => openDrillDown('Active Leads', l => l.stage !== 'Closed Won' && l.stage !== 'Closed Lost')} />
            <StatCard label="Closed Won" value={won} color="#16a34a"
              onClick={() => openDrillDown('Closed Won', l => l.stage === 'Closed Won')} />
            <StatCard label="Follow-ups Due" value={followUps.length} color={followUps.length > 0 ? '#dc2626' : '#374151'}
              onClick={() => openDrillDown('Follow-ups Due', l => l.follow_up_date && new Date(l.follow_up_date) < new Date() && l.stage !== 'Closed Won' && l.stage !== 'Closed Lost')} />
          </div>

          <div style={{ display: 'flex', gap: 14, marginBottom: 24, flexWrap: 'wrap' }}>
            {STAGES.map(s => {
              const c = STAGE_COLORS[s];
              return (
                <div key={s} onClick={() => openDrillDown(s, l => l.stage === s)} style={{
                  background: c.bg, border: `1px solid ${c.border}`, borderRadius: 8,
                  padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
                }}
                  onMouseEnter={e => e.currentTarget.style.opacity = '0.8'}
                  onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                >
                  <span style={{ fontSize: 18, fontWeight: 700, color: c.text }}>{summary[s] || 0}</span>
                  <span style={{ fontSize: 12, color: c.text, fontWeight: 500 }}>{s}</span>
                </div>
              );
            })}
          </div>

          <div style={{ marginBottom: 28 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>Import Demo Leads</div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {IMPORT_SOURCES.map(({ source, label, color }) => (
                <button key={source} onClick={() => handleImport(source, label)} disabled={importingSource === source} style={{
                  padding: '9px 18px', background: color, color: 'white', border: 'none',
                  borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: importingSource === source ? 0.7 : 1,
                }}>
                  {importingSource === source ? 'Importing...' : `Import from ${label}`}
                </button>
              ))}
            </div>
          </div>

          {followUps.length > 0 && (
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 12 }}>Follow-ups Due</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {followUps.slice(0, 10).map(lead => (
                  <div key={lead.id} onClick={() => onLeadClick(lead.id)} style={{
                    background: 'white', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  }}>
                    <div>
                      <span style={{ fontWeight: 600, color: '#0f172a', fontSize: 13 }}>{lead.name}</span>
                      {lead.org_name && <span style={{ color: '#64748b', fontSize: 12, marginLeft: 8 }}>{lead.org_name}</span>}
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <StageBadge stage={lead.stage} small />
                      <span style={{ fontSize: 11, color: '#dc2626', fontWeight: 600 }}>Due {new Date(lead.follow_up_date).toLocaleDateString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {drillDown && (
            <DrillDownPanel
              title={drillDown.title}
              leads={drillDown.leads}
              onLeadClick={onLeadClick}
              onClose={() => setDrillDown(null)}
            />
          )}
        </>
      )}
    </div>
  );
}

// ============================================================
// USER MANAGEMENT
// ============================================================
function UserManagement({ token, user, showToast }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'user' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    const d = await apiCall('/api/users', 'GET', null, token);
    if (d.success) setUsers(d.users);
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!form.name || !form.email || !form.password) { setError('Name, email, and password required'); return; }
    setSaving(true);
    const d = await apiCall('/api/users', 'POST', form, token);
    if (d.success) {
      showToast('User added');
      setForm({ name: '', email: '', password: '', role: 'user' });
      load();
    } else { setError(d.error || 'Failed to add user'); }
    setSaving(false);
  };

  const handleToggleActive = async (u) => {
    await apiCall(`/api/users/${u.id}`, 'PUT', { active: u.active ? 0 : 1 }, token);
    showToast(u.active ? 'User deactivated' : 'User activated');
    load();
  };

  const inp = { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };

  return (
    <div>
      <div style={{ fontSize: 18, fontWeight: 700, color: '#0f172a', marginBottom: 20 }}>User Management</div>

      {(user.role === 'admin' || user.role === 'superadmin') && (
        <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 10, padding: 20, marginBottom: 24 }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 14 }}>Add User</div>
          {error && <div style={{ color: '#dc2626', fontSize: 12, marginBottom: 10 }}>{error}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 14px' }}>
            <div><label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>NAME</label><input style={inp} value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} /></div>
            <div><label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>EMAIL</label><input style={inp} type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} /></div>
            <div><label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>PASSWORD</label><input style={inp} type="password" value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} /></div>
            <div><label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>ROLE</label>
              <select style={inp} value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value }))}>
                <option value="user">User</option>
                <option value="admin">Admin</option>
                {user.role === 'superadmin' && <option value="superadmin">Superadmin</option>}
              </select>
            </div>
          </div>
          <button onClick={handleAdd} disabled={saving} style={{ marginTop: 14, padding: '8px 20px', background: '#1e3a5f', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            {saving ? 'Adding...' : 'Add User'}
          </button>
        </div>
      )}

      {loading ? <div style={{ color: '#64748b' }}>Loading...</div> : (
        <div style={{ background: 'white', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: '#f8fafc' }}>
              <tr>
                {['Name', 'Email', 'Role', 'Org', 'Last Login', 'Status', ''].map(h => (
                  <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '2px solid #e2e8f0' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id}>
                  <td style={{ padding: '10px 12px', fontSize: 13, fontWeight: 600, color: '#0f172a', borderBottom: '1px solid #f1f5f9' }}>{u.name}</td>
                  <td style={{ padding: '10px 12px', fontSize: 13, color: '#374151', borderBottom: '1px solid #f1f5f9' }}>{u.email}</td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9' }}>
                    <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: u.role === 'superadmin' ? '#fef3c7' : u.role === 'admin' ? '#eff6ff' : '#f8fafc', color: u.role === 'superadmin' ? '#92400e' : u.role === 'admin' ? '#1e40af' : '#374151' }}>
                      {u.role}
                    </span>
                  </td>
                  <td style={{ padding: '10px 12px', fontSize: 13, color: '#64748b', borderBottom: '1px solid #f1f5f9' }}>{u.org_name}</td>
                  <td style={{ padding: '10px 12px', fontSize: 12, color: '#94a3b8', borderBottom: '1px solid #f1f5f9' }}>{u.last_login ? new Date(u.last_login).toLocaleDateString() : 'Never'}</td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9' }}>
                    <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: u.active ? '#f0fdf4' : '#fef2f2', color: u.active ? '#16a34a' : '#dc2626' }}>
                      {u.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9' }}>
                    {u.id !== user.id && (
                      <button onClick={() => handleToggleActive(u)} style={{ fontSize: 11, padding: '3px 10px', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer', background: 'white', color: '#374151' }}>
                        {u.active ? 'Deactivate' : 'Activate'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


// ============================================================
// ARCHIVED LEADS VIEW
// ============================================================
function ArchivedView({ token, showToast, onLeadClick }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoreId, setRestoreId] = useState(null);
  const [restoreReason, setRestoreReason] = useState('');
  const [restoring, setRestoring] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const d = await apiCall('/api/leads/archived', 'GET', null, token);
    if (d.success) setLeads(d.leads);
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handleRestore = async () => {
    if (!restoreReason.trim()) return;
    setRestoring(true);
    const d = await apiCall(`/api/leads/${restoreId}/restore`, 'PATCH', { reason: restoreReason }, token);
    if (d.success) {
      showToast('Lead restored', 'Lead has been returned to the active pipeline');
      setRestoreId(null);
      setRestoreReason('');
      load();
    } else { showToast('Error', d.error, 'error'); }
    setRestoring(false);
  };

  const th = { padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap' };
  const td = { padding: '10px 12px', fontSize: 13, color: '#374151', borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle' };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#0f172a' }}>Archived Leads</div>
        <div style={{ fontSize: 13, color: '#64748b' }}>{leads.length} archived lead{leads.length !== 1 ? 's' : ''}</div>
      </div>

      {loading ? <div style={{ color: '#64748b' }}>Loading...</div> : (
        <div style={{ background: 'white', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          {leads.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8', fontStyle: 'italic' }}>No archived leads.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ background: '#f8fafc' }}>
                <tr>
                  <th style={th}>Name</th>
                  <th style={th}>Organization</th>
                  <th style={th}>Source</th>
                  <th style={th}>Last Stage</th>
                  <th style={th}>Archive Reason</th>
                  <th style={th}>Archived</th>
                  <th style={th}>Archived By</th>
                  <th style={th}></th>
                </tr>
              </thead>
              <tbody>
                {leads.map(lead => (
                  <tr key={lead.id}
                    onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}>
                    <td style={td}>
                      <div style={{ fontWeight: 600, color: '#0f172a', cursor: 'pointer', textDecoration: 'underline' }}
                        onClick={() => onLeadClick(lead.id)}>{lead.name}</div>
                      {lead.email && <div style={{ fontSize: 11, color: '#94a3b8' }}>{lead.email}</div>}
                    </td>
                    <td style={td}>{lead.org_name || <span style={{ color: '#cbd5e1' }}>--</span>}</td>
                    <td style={td}><SourceBadge source={lead.source} /></td>
                    <td style={td}><StageBadge stage={lead.stage} small /></td>
                    <td style={{ ...td, color: '#6b7280' }}>{lead.archived_reason || '--'}</td>
                    <td style={{ ...td, color: '#94a3b8', fontSize: 12 }}>
                      {lead.archived_at ? new Date(lead.archived_at).toLocaleDateString() : '--'}
                    </td>
                    <td style={{ ...td, color: '#94a3b8' }}>{lead.archived_by_name || '--'}</td>
                    <td style={td}>
                      <button onClick={() => { setRestoreId(lead.id); setRestoreReason(''); }} style={{
                        padding: '4px 12px', background: '#f0fdf4', color: '#16a34a', border: '1px solid #86efac',
                        borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600,
                      }}>Restore</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {restoreId && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
          onClick={() => setRestoreId(null)}>
          <div style={{ background: 'white', borderRadius: 12, padding: 28, width: '100%', maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>Restore Lead</div>
            <div style={{ fontSize: 13, color: '#64748b', marginBottom: 20 }}>This lead will be returned to the active pipeline as a New Lead.</div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Reason for Restoring *</label>
              <select style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }}
                value={restoreReason} onChange={e => setRestoreReason(e.target.value)}>
                <option value="">Select a reason...</option>
                <option>Re-engaged after outreach</option>
                <option>Budget now available</option>
                <option>Timing is now right</option>
                <option>Archived in error</option>
                <option>Referral reactivation</option>
                <option>Other</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={handleRestore} disabled={restoring || !restoreReason} style={{
                flex: 1, padding: '9px', background: '#16a34a', color: 'white', border: 'none', borderRadius: 8,
                fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: (!restoreReason || restoring) ? 0.5 : 1,
              }}>
                {restoring ? 'Restoring...' : 'Restore Lead'}
              </button>
              <button onClick={() => setRestoreId(null)} style={{ padding: '9px 18px', background: '#f1f5f9', border: 'none', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// MAIN APP
// ============================================================
export default function App() {
  const [token, setToken] = useState(localStorage.getItem('pipeline_token'));
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [forceReset, setForceReset] = useState(false);
  const [view, setView] = useState('dashboard');
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const [newLeadOpen, setNewLeadOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const { toasts, show: showToast, remove: removeToast } = useToast();

  // Verify token on mount
  useEffect(() => {
    if (!token) { setAuthLoading(false); return; }
    apiCall('/api/auth/me', 'GET', null, token).then(d => {
      if (d.success) {
        setUser(d.user);
        if (d.forcePasswordReset) setForceReset(true);
      }
      else { localStorage.removeItem('pipeline_token'); setToken(null); }
      setAuthLoading(false);
    }).catch(() => { setAuthLoading(false); });
  }, [token]);

  // Load users for assignment dropdowns
  useEffect(() => {
    if (!token || !user) return;
    if (user.role === 'admin' || user.role === 'superadmin') {
      apiCall('/api/users', 'GET', null, token).then(d => { if (d.success) setUsers(d.users); });
    }
  }, [token, user]);

  const handleLogin = (u, t, fpr) => { setUser(u); setToken(t); if (fpr) setForceReset(true); };
  const handleLogout = () => { localStorage.removeItem('pipeline_token'); setToken(null); setUser(null); };

  if (authLoading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif", color: '#64748b' }}>
      Loading...
    </div>
  );

  if (!token || !user) return <LoginPage onLogin={handleLogin} />;
  if (forceReset) return <ForcePasswordReset token={token} onComplete={() => setForceReset(false)} />;

  const canAdmin = user.role === 'admin' || user.role === 'superadmin';

  const NAV = [
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'pipeline', label: 'Pipeline' },
    { id: 'list', label: 'All Leads' },
    { id: 'archived', label: 'Archived' },
    ...(canAdmin ? [{ id: 'users', label: 'Users' }] : []),
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#f1f5f9', fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif" }}>
      {/* Header */}
      <div style={{ background: '#0f172a', borderBottom: '1px solid #1e293b', padding: '0 24px', display: 'flex', alignItems: 'center', height: 56, gap: 0 }}>
        <div style={{ marginRight: 32 }}>
          <span style={{ fontSize: 11, letterSpacing: 3, color: '#64748b', textTransform: 'uppercase' }}>SchoolLaunch</span>
          <span style={{ fontSize: 15, fontWeight: 700, color: 'white', marginLeft: 8 }}>Pipeline</span>
        </div>
        <div style={{ display: 'flex', gap: 2, flex: 1 }}>
          {NAV.map(n => (
            <button key={n.id} onClick={() => setView(n.id)} style={{
              padding: '6px 14px', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer',
              background: view === n.id ? '#1e293b' : 'transparent',
              color: view === n.id ? 'white' : '#94a3b8',
              fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
            }}>{n.label}</button>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {view !== 'users' && view !== 'archived' && (
            <button onClick={() => setNewLeadOpen(true)} style={{
              padding: '6px 14px', background: '#1e3a5f', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer',
            }}>+ Add Lead</button>
          )}
          <div style={{ fontSize: 12, color: '#64748b' }}>{user.name}</div>
          <div style={{ fontSize: 11, color: '#475569', background: '#1e293b', padding: '2px 8px', borderRadius: 10 }}>{user.org_name}</div>
          <button onClick={handleLogout} style={{ padding: '5px 12px', background: '#1e293b', color: '#94a3b8', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>Sign Out</button>
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: '24px 28px', maxWidth: 1400, margin: '0 auto' }}>
        {view === 'dashboard' && (
          <Dashboard token={token} showToast={showToast} onImport={() => {}} onLeadClick={setSelectedLeadId} />
        )}
        {view === 'pipeline' && (
          <PipelineView token={token} users={users} showToast={showToast} onLeadClick={setSelectedLeadId} />
        )}
        {view === 'list' && (
          <ListView token={token} users={users} showToast={showToast} onLeadClick={setSelectedLeadId} onNewLead={() => setNewLeadOpen(true)} />
        )}
        {view === 'users' && canAdmin && (
          <UserManagement token={token} user={user} showToast={showToast} />
        )}
        {view === 'archived' && (
          <ArchivedView token={token} showToast={showToast} onLeadClick={setSelectedLeadId} />
        )}
      </div>

      {/* Lead detail panel */}
      {selectedLeadId && (
        <LeadDetail
          leadId={selectedLeadId} token={token} users={users} showToast={showToast}
          onClose={() => setSelectedLeadId(null)}
          onUpdated={() => {}}
        />
      )}

      {/* New lead modal */}
      {newLeadOpen && (
        <LeadFormModal
          token={token} users={users}
          onSave={(lead) => { setNewLeadOpen(false); showToast('Lead added', lead.name); setSelectedLeadId(lead.id); }}
          onClose={() => setNewLeadOpen(false)}
        />
      )}

      <Toast toasts={toasts} remove={removeToast} />
    </div>
  );
}
